import { TranslocoService } from '@ngneat/transloco';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { Subscription } from 'rxjs';
import { BaseFormList } from 'projects/admin/src/app/classes/base-form-list';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormModelModel } from 'projects/core/src/lib/models/form-model.model';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';
import { BusinessunitModel } from 'projects/core/src/lib/models/businessunit.model';
import { BaseModel } from 'projects/core/src/lib/models/base.model';

const NOEVENT = { emitEvent: false };

export enum SettingsUnitsMode {
  Businessunits = 'businessunits',
  Templates = 'templates',
}

class LineModel extends BaseModel {
  public templateId: number;
  public businessunitId: number;
  public name: string;
  public isheadline: boolean = false;
}

interface MonthEntry {
  month: string;
  label: string;
  limit: UntypedFormControl | null;
  booked: number;
  isPast: boolean;
  originalLimit: number | null;
}

interface OpenedEntry {
  templateName: string;
  businessunitName: string;
  futureMonths: MonthEntry[];
  pastMonths: MonthEntry[];
  rowKey: string;
  templateId: number;
  templateLimits: { [month: string]: number | null };
  templateBookingCounts: { [month: string]: number };
}

@Component({
  selector: 'app-settings-units',
  templateUrl: './settings-units.component.html',
  styleUrls: ['./settings-units.component.scss']
})
export class SettingsUnitsComponent extends BaseFormList implements OnInit {

  public models: Array<FormModelModel> = [];
  public optionsModes: Array<SelectItem> = [];
  public optionsBusinessunits: Array<SelectItemGroup> = [];
  public form: UntypedFormGroup = this.fb.group({
    mode: new UntypedFormControl(''),
    businessunit: new UntypedFormControl(''),
    template: new UntypedFormControl(''),
  });
  public mode: SettingsUnitsMode = null;
  public modes = SettingsUnitsMode;
  public businessunits: Array<BusinessunitModel> = [];
  public templates: Array<TemplateModel> = [];
  public settings: Array<any> = [];
  public title: string = null;

  public openedEntry: OpenedEntry | null = null;
  public showPastMonths = false;

  protected rightLoad = 'admin.planning-settings.show-settings';

  private subscriptions: Array<Subscription> = [];
  private monthlyLimitsMap = new Map<string, { [month: string]: number | null }>();
  private monthlyLimitsSnapshot = new Map<string, { [month: string]: number | null }>();
  private bookingCountsMap = new Map<string, { [month: string]: number }>();
  private templateBookingCountsMap = new Map<number, { [month: string]: number }>();
  private templateMonthlyLimitsMap = new Map<number, { [month: string]: number | null }>();

  constructor(
    protected router: Router,
    protected ui: UiService,
    protected helper: HelperService,
    protected api: ApiService,
    protected auth: AuthStateService,
    protected fb: UntypedFormBuilder,
    public transloco: TranslocoService
  ) {
    super(router, ui, helper, transloco, auth, fb);
  }

  ngOnInit(): void {
    this.loadList();
  }

  ngOnDestroy(): void {
    this.helper.unsetSubscriptions(this.subscriptions);
  }

  public loadList(): void {
    this.loading = true;
    this.api.getTemplateSettings('units').subscribe({
      next: (result: any) => {
        this.formConfig = result.data.config;
        this.initBunsinessunits(result.data.businessunits);
        this.initTemplates(result.data.templates);
        this.setOptions(result.data.options);
        this.setSettings(result.data.settings);
        this.initSubscriptions();
        this.initModes();
    },
      error: (error: any) => {

      }
    }).add(() => {
      this.loading = false;
    });
  }

  public onEdit(): void {
    this.monthlyLimitsSnapshot = new Map(this.monthlyLimitsMap);
    this.disableFilter();
    this.inEdit = true;
    this.enableControls();
  }

  public onCancel(): void {
    this.monthlyLimitsMap = new Map(this.monthlyLimitsSnapshot);
    this.enableFilter();
    this.inEdit = false;
    this.disableControls();
    this.resetControls();
    this.ui.hideMessage();
  }

  public onSubmit(): void {
    if (this.isValid()) {
      this.ui.hideMessage();
      this.submitting = true;
      const businessunitId = this.form.get('businessunit').value;
      const templateId = this.form.get('template').value?.id;
      const payload: any = {
        mode: this.mode,
        id: (this.mode === SettingsUnitsMode.Businessunits) ? businessunitId : templateId,
        data: {}
      };
      this.models.forEach((formTemplate: FormModelModel) => {
        if (formTemplate.model.isheadline !== true) {
          const id = (this.mode === SettingsUnitsMode.Businessunits) ? formTemplate.model.templateId : formTemplate.model.businessunitId;
          const buId = (this.mode === SettingsUnitsMode.Businessunits) ? businessunitId : formTemplate.model.businessunitId;
          const tplId = (this.mode === SettingsUnitsMode.Businessunits) ? formTemplate.model.templateId : templateId;
          const rowKey = `${buId}-${tplId}`;
          const monthly_limits = this.monthlyLimitsMap.get(rowKey) ?? {};
          payload.data[id] = { ...this.getPayload(formTemplate), monthly_limits };
        }
      });
      this.api.saveTemplateSettings('units', payload).subscribe({
        next: (result: any) => {
          this.inEdit = false;
          this.disableControls();
          this.setSettings(result.data);
          this.buildList();
          this.enableFilter();
          this.ui.toastSuccess(this.transloco.translate('common.messages.settings_saved'), this.transloco.translate('common.saved'));
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          this.setBackendErrors(error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.markAsDirty();
    }
  }

  public hasMonthlyLimits(formTemplate: FormModelModel): boolean {
    const buId = formTemplate.model.businessunitId;
    const tplId = formTemplate.model.templateId;
    const rowKey = `${buId}-${tplId}`;
    const limits = this.monthlyLimitsMap.get(rowKey) ?? {};
    return Object.keys(limits).length > 0;
  }

  public openMonthlyLimitsDialog(formTemplate: FormModelModel): void {
    const buId = formTemplate.model.businessunitId;
    const tplId = formTemplate.model.templateId;
    const rowKey = `${buId}-${tplId}`;

    const storedLimits = this.monthlyLimitsMap.get(rowKey) ?? {};
    const bookingCounts = this.bookingCountsMap.get(rowKey) ?? {};

    const now = new Date();
    const futureMonths: MonthEntry[] = [];
    const pastMonths: MonthEntry[] = [];

    // Build current + next 11 months
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const month = this.formatYearMonth(d);
      const label = d.toLocaleDateString(this.transloco.getActiveLang(), { month: 'long', year: 'numeric' });
      const originalLimit = storedLimits[month] != null ? Number(storedLimits[month]) : null;
      futureMonths.push({
        month,
        label,
        limit: new UntypedFormControl(originalLimit),
        booked: bookingCounts[month] ?? 0,
        isPast: false,
        originalLimit,
      });
    }

    // Build past months (only those with bookings or a stored limit)
    const allMonths = new Set<string>([
      ...Object.keys(storedLimits),
      ...Object.keys(bookingCounts),
    ]);
    allMonths.forEach((month) => {
      if (month >= this.formatYearMonth(now)) return;
      const booked = bookingCounts[month] ?? 0;
      const originalLimit = storedLimits[month] != null ? Number(storedLimits[month]) : null;
      if (originalLimit === null) return;
      const parts = month.split('-');
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
      const label = d.toLocaleDateString(this.transloco.getActiveLang(), { month: 'long', year: 'numeric' });
      pastMonths.push({ month, label, limit: null, booked, isPast: true, originalLimit });
    });
    pastMonths.sort((a, b) => b.month.localeCompare(a.month));

    const templateName = (this.mode === SettingsUnitsMode.Businessunits)
      ? formTemplate.model.name
      : this.title;
    const businessunitName = (this.mode === SettingsUnitsMode.Businessunits)
      ? this.title
      : formTemplate.model.name;

    this.showPastMonths = false;
    const templateLimits = this.templateMonthlyLimitsMap.get(tplId) ?? {};
    const templateBookingCounts = this.templateBookingCountsMap.get(tplId) ?? {};
    this.openedEntry = {
      templateName,
      businessunitName,
      futureMonths,
      pastMonths,
      rowKey,
      templateId: tplId,
      templateLimits,
      templateBookingCounts,
    };
  }

  public clampLimitToBooked(entry: MonthEntry): void {
    if (entry.limit === null) return;
    const val = entry.limit.value;
    if (val !== null && val < entry.booked) {
      entry.limit.setValue(entry.booked);
    }
  }

  public saveMonthlyLimitsDialog(): void {
    if (!this.openedEntry) return;

    const limits: { [month: string]: number | null } = {};
    this.openedEntry.futureMonths.forEach((entry) => {
      this.clampLimitToBooked(entry);
      const val = entry.limit.value;
      if (val !== null && val !== '') {
        limits[entry.month] = Number(val);
      }
    });

    this.monthlyLimitsMap.set(this.openedEntry.rowKey, limits);
    this.openedEntry = null;
  }

  public closeMonthlyLimitsDialog(): void {
    this.openedEntry = null;
  }

  public hasConflictWarning(): boolean {
    if (!this.openedEntry) return false;
    return this.openedEntry.futureMonths.some((entry) => this.hasTemplateLimitConflict(entry.month));
  }

  public hasTemplateLimitConflict(month: string): boolean {
    if (!this.openedEntry) return false;
    const templateLimit = this.openedEntry.templateLimits[month];
    if (templateLimit == null) return false;
    return this.sumBuLimitsForMonth(month) > templateLimit;
  }

  public getOtherBuLimitsLabel(month: string): string {
    const other = this.sumOtherBuLimitsForMonth(month);
    return other.hasAny ? String(other.sum) : '—';
  }

  /**
   * Sum BU limits for one template/month across all business units.
   * Sibling rows use monthlyLimitsMap (values persisted via saveMonthlyLimitsDialog).
   * The row currently open uses live form controls — the map is not updated until save.
   */
  private sumBuLimitsForMonth(month: string): number {
    let sum = this.sumOtherBuLimitsForMonth(month).sum;
    const opened = this.openedEntry;
    if (!opened) return sum;
    const live = opened.futureMonths.find((entry) => entry.month === month);
    if (live?.limit != null) {
      const val = live.limit.value;
      if (val != null && val !== '') {
        sum += Number(val);
      }
    }
    return sum;
  }

  private sumOtherBuLimitsForMonth(month: string): { sum: number; hasAny: boolean } {
    let sum = 0;
    let hasAny = false;
    const opened = this.openedEntry;
    if (!opened) return { sum, hasAny };
    const templateId = opened.templateId;
    this.monthlyLimitsMap.forEach((limits, rowKey) => {
      if (!rowKey.endsWith(`-${templateId}`)) return;
      if (rowKey === opened.rowKey) return;
      const val = limits[month];
      if (val != null) {
        sum += Number(val);
        hasAny = true;
      }
    });
    return { sum, hasAny };
  }

  public getTemplateTotalLabel(month: string): string {
    if (!this.openedEntry) return '—';
    const limit = this.openedEntry.templateLimits[month];
    return limit != null ? String(limit) : '—';
  }

  private formatYearMonth(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }

  private buildList(): void {
    this.clearControls();
    this.clearModels();
    this.saveMode();
    const businessunitId = this.form.get('businessunit').value;
    const templateId = this.form.get('template').value?.id;
    if (this.mode === SettingsUnitsMode.Businessunits && businessunitId) {
      // map templates
      this.title = this.getBusinessunitName(businessunitId);
      this.models = this.templates.map((model: TemplateModel) => {
        const lineModel = new LineModel().deserialize({
          businessunitId: businessunitId,
          templateId: model.id,
          name: model.name,
          updated_at: model.updated_at,
          updated_from: model.updated_from,
        });
        const formTemplate = new FormModelModel(lineModel);
        const rawSetting = this.getRawSetting(businessunitId, model.id);
        this.addControls(formTemplate.initForm(this.formConfig, rawSetting ? rawSetting.settings : {}));
        const rowKey = `${businessunitId}-${model.id}`;
        if (!this.monthlyLimitsMap.has(rowKey)) {
          this.monthlyLimitsMap.set(rowKey, rawSetting?.settings?.monthly_limits ?? {});
        }
        return formTemplate;
      }).sort((a: FormModelModel, b: FormModelModel) => {
        return (a.model.name > b.model.name) ? 1 : ((b.model.name > a.model.name) ? -1 : 0);
      });
    } else if (this.mode === SettingsUnitsMode.Templates && templateId) {
      // map businessunits
      this.title = this.form.get('template').value?.name;
      this.models = [];
      this.businessunits.forEach((model: BusinessunitModel) => {
        const lineModel = new LineModel().deserialize({
          name: model.name,
          isheadline: true
        });
        this.models.push(new FormModelModel(lineModel));
        model.businessunits.forEach((child: BusinessunitModel) => {
          const lineModel = new LineModel().deserialize({
            businessunitId: child.id,
            templateId: templateId,
            name: child.name,
            updated_at: child.updated_at,
            updated_from: child.updated_from,
          });
          const formTemplate = new FormModelModel(lineModel);
          const rawSetting = this.getRawSetting(child.id, templateId);
          this.addControls(formTemplate.initForm(this.formConfig, rawSetting ? rawSetting.settings : {}));
          const rowKey = `${child.id}-${templateId}`;
          if (!this.monthlyLimitsMap.has(rowKey)) {
            this.monthlyLimitsMap.set(rowKey, rawSetting?.settings?.monthly_limits ?? {});
          }
          this.models.push(formTemplate);
          });
      });
    }
  }

  private setSettings(settings: Array<any>): void {
    this.settings = settings;
    settings.forEach((s: any) => {
      const key = `${s.businessunit_id}-${s.ac_id}`;
      this.bookingCountsMap.set(key, s.booking_counts ?? {});
      this.monthlyLimitsMap.set(key, s.settings?.monthly_limits ?? {});
    });
  }

  private getRawSetting(businessunitId: any, templateId: any): any {
    return this.settings.find((actual: any) => {
      actual.settings.active = actual.active;
      return actual.ac_id === templateId && actual.businessunit_id === businessunitId;
    });
  }

  private initSubscriptions(): void {
    this.subscriptions = [];
    this.subscriptions.push(
      this.form.get('mode').valueChanges
        .subscribe(() => {
          this.setMode();
        })
    );
    this.subscriptions.push(
      this.form.get('businessunit').valueChanges
        .subscribe(() => {
          this.buildList();
        })
    );
    this.subscriptions.push(
      this.form.get('template').valueChanges
        .subscribe(() => {
          this.buildList();
        })
    );
  }

  private disableFilter(): void {
    this.form.get('mode').disable(NOEVENT);
    this.form.get('businessunit').disable(NOEVENT);
    this.form.get('template').disable(NOEVENT);
  }

  private enableFilter(): void {
    this.form.get('mode').enable(NOEVENT);
    this.form.get('businessunit').enable(NOEVENT);
    this.form.get('template').enable(NOEVENT);
  }

  private initModes(): void {
    this.optionsModes = [
      {
        value: SettingsUnitsMode.Businessunits,
        label: this.transloco.translate('common.labels.' + SettingsUnitsMode.Businessunits),
      },
      {
        value: SettingsUnitsMode.Templates,
        label: this.transloco.translate('common.labels.' + SettingsUnitsMode.Templates),
      },
    ];
    let initMode = this.optionsModes[0].value;
    const savedMode = localStorage.getItem('settings-units-mode');
    if (savedMode) {
      initMode = this.optionsModes.find(option => option.value === savedMode)?.value;
    }
    this.form.get('mode').setValue(initMode);
  }

  private setMode(): void {
    this.mode = this.form.get('mode').value;
    if (!this.form.get('template').value) {
      this.form.get('template').setValue(this.templates[0]);
    }
    if (!this.form.get('businessunit').value
      && this.businessunits[0]
      && Array.isArray(this.businessunits[0].businessunits)
      && this.businessunits[0].businessunits[0]
    ) {
      this.form.get('businessunit').setValue(this.businessunits[0].businessunits[0].id);
    }
    this.buildList();
  }

  private initBunsinessunits(businessunits: Array<any>): void {
    this.businessunits = businessunits.map((businessunit: any) => {
      return new BusinessunitModel().deserialize(businessunit);
    });
    this.optionsBusinessunits = this.helper.getGroupOptions(this.businessunits, 'name', 'businessunits');
  }

  private initTemplates(templates: Array<any>): void {
    this.templates = templates.map((template: any) => {
      this.templateBookingCountsMap.set(template.id, template.booking_counts ?? {});
      this.templateMonthlyLimitsMap.set(template.id, template.settings?.monthly_limits ?? {});
      return new TemplateModel().deserialize(template);
    });
  }

  private getBusinessunitName(id: number): string | null {
    let name = null;
    this.businessunits.forEach((businessunit: BusinessunitModel) => {
      if (businessunit.id === id) {
        name = businessunit.name;
      }
      businessunit.businessunits.forEach((child: BusinessunitModel) => {
        if (child.id === id) {
          name = child.name;
        }
      });
    });
    return name;
  }

  private saveMode(): void {
    if (this.loading !== true) {
      if (this.mode) {
        localStorage.setItem('settings-units-mode', this.mode);
      }
    }
  }

}
